import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api, ApiError, type TokenPair } from '@/lib/api'

export interface User {
  id: string
  email: string
  full_name: string
  country: string
  status: string
  email_verified: boolean
  created_at: string
}

interface AuthResponse {
  user: User
  tokens: TokenPair
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<ApiError | null>(null)
  // Distinguishes "not signed in" from "we have not checked yet", which is
  // what stops the router bouncing a signed-in user to the login page on a
  // hard refresh before the profile has loaded.
  const initialised = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const canTradeReal = computed(
    () => user.value?.status === 'active' && user.value.email_verified,
  )

  /** Restores the session on page load, if a stored token still works. */
  async function initialise(): Promise<void> {
    if (initialised.value) return
    if (!api.isAuthenticated) {
      initialised.value = true
      return
    }
    try {
      user.value = await api.get<User>('/api/v1/users/me')
    } catch {
      // An expired or revoked token: the client already tried to refresh and
      // failed, so there is nothing to recover.
      api.setTokens(null)
      user.value = null
    } finally {
      initialised.value = true
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      const result = await api.post<AuthResponse>('/api/v1/auth/login', { email, password })
      api.setTokens(result.tokens)
      user.value = result.user
      return true
    } catch (err) {
      error.value = err as ApiError
      return false
    } finally {
      loading.value = false
    }
  }

  async function register(input: {
    email: string
    password: string
    confirm_password: string
    full_name: string
    country: string
  }): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      const result = await api.post<AuthResponse>('/api/v1/auth/register', {
        ...input,
        accept_terms: true,
      })
      api.setTokens(result.tokens)
      user.value = result.user
      return true
    } catch (err) {
      error.value = err as ApiError
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout')
    } catch {
      // The session is being abandoned either way. A failed logout call is
      // not a reason to leave someone signed in on their own screen.
    } finally {
      api.setTokens(null)
      user.value = null
    }
  }

  /**
   * Drops the local session without calling the server.
   *
   * For the case where the session is already gone - the server rejected the
   * refresh token - so there is nothing to revoke and the logout call would
   * only 401 in turn. logout() is for a trader who chose to leave; this is for
   * a session that ended on its own.
   */
  function forget(): void {
    api.setTokens(null)
    user.value = null
  }

  return {
    user, loading, error, initialised,
    isAuthenticated, canTradeReal,
    initialise, login, register, logout, forget,
  }
})
